import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import SignIn from "@/pages/SignIn";
import ClaimAccount from "@/pages/ClaimAccount";
import ResetRequest from "@/pages/ResetRequest";
import ResetPassword from "@/pages/ResetPassword";
import Verify from "@/pages/Verify";
import Pass from "@/pages/Pass";
import Profile from "@/pages/Profile";
import Privileges from "@/pages/Privileges";
import PartnerDetail from "@/pages/PartnerDetail";
import Home from "@/pages/Home";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Games from "@/pages/Games";
import GameDetail from "@/pages/GameDetail";
import GameCreate from "@/pages/GameCreate";
import Preferences from "@/pages/Preferences";
import Privacy from "@/pages/Privacy";
import Consent from "@/pages/onboarding/Consent";
import Complete from "@/pages/onboarding/Complete";
import MemberList from "@/pages/admin/MemberList";
import MemberNew from "@/pages/admin/MemberNew";
import MemberDetail from "@/pages/admin/MemberDetail";
import PartnerList from "@/pages/admin/PartnerList";
import PartnerNew from "@/pages/admin/PartnerNew";
import PartnerEdit from "@/pages/admin/PartnerEdit";
import ChapterList from "@/pages/admin/ChapterList";
import ChapterEdit from "@/pages/admin/ChapterEdit";
import EventList from "@/pages/admin/EventList";
import EventNew from "@/pages/admin/EventNew";
import EventEdit from "@/pages/admin/EventEdit";
import ContentList from "@/pages/admin/ContentList";
import ContentNew from "@/pages/admin/ContentNew";
import ContentEdit from "@/pages/admin/ContentEdit";

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth/sign-in" replace />;
  return <Layout><Outlet /></Layout>;
}

function AdminRoute() {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth/sign-in" replace />;
  if (!isAdmin) return <Navigate to="/pass" replace />;
  return <Layout><Outlet /></Layout>;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth/sign-in" replace />;
  return <Outlet />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-lv-bg">
      <div className="text-center">
        <h1 className="font-serif text-2xl text-cream tracking-[0.25em]" data-testid="loading-screen">LV PASS</h1>
        <div className="mt-4 w-8 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth/sign-in" element={<SignIn />} />
          <Route path="/auth/claim/:token" element={<ClaimAccount />} />
          <Route path="/auth/reset" element={<ResetRequest />} />
          <Route path="/auth/reset/:token" element={<ResetPassword />} />
          <Route path="/verify/:token" element={<Verify />} />
          <Route element={<OnboardingRoute />}>
            <Route path="/onboarding/consent" element={<Consent />} />
            <Route path="/onboarding/complete" element={<Complete />} />
          </Route>
          <Route element={<ProtectedRoute />}>
            <Route path="/home" element={<Home />} />
            <Route path="/pass" element={<Pass />} />
            <Route path="/me" element={<Profile />} />
            <Route path="/privileges" element={<Privileges />} />
            <Route path="/privileges/:partnerId" element={<PartnerDetail />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:eventId" element={<EventDetail />} />
            <Route path="/games" element={<Games />} />
            <Route path="/games/new" element={<GameCreate />} />
            <Route path="/games/:gameId" element={<GameDetail />} />
            <Route path="/preferences" element={<Preferences />} />
            <Route path="/privacy" element={<Privacy />} />
          </Route>
          <Route element={<AdminRoute />}>
            <Route path="/admin/members" element={<MemberList />} />
            <Route path="/admin/members/new" element={<MemberNew />} />
            <Route path="/admin/members/:id" element={<MemberDetail />} />
            <Route path="/admin/partners" element={<PartnerList />} />
            <Route path="/admin/partners/new" element={<PartnerNew />} />
            <Route path="/admin/partners/:id" element={<PartnerEdit />} />
            <Route path="/admin/chapters" element={<ChapterList />} />
            <Route path="/admin/chapters/:id" element={<ChapterEdit />} />
            <Route path="/admin/events" element={<EventList />} />
            <Route path="/admin/events/new" element={<EventNew />} />
            <Route path="/admin/events/:id" element={<EventEdit />} />
            <Route path="/admin/content" element={<ContentList />} />
            <Route path="/admin/content/new" element={<ContentNew />} />
            <Route path="/admin/content/:id" element={<ContentEdit />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </AuthProvider>
  );
}

export default App;
